import type { Socket } from 'socket.io-client';

type KickedSocket = Pick<Socket, 'on' | 'connect' | 'connected'>;
/** 'gone' only for an answer that the session no longer exists (401);
 *  an outage or a network error is 'unknown' and changes nothing. */
export type SessionVerdict = 'live' | 'gone' | 'unknown';

/** A kick soon after our own reconnect waits longer each time, up to a minute. */
export const KICK_QUIET_MS = 60_000;
const FIRST_DELAY_MS = 1_000;
const MAX_DELAY_MS = 60_000;

const bound = new WeakSet<object>();

/**
 * When the server closes the socket ('io server disconnect') the session may
 * be gone — revoked, expired, signed out elsewhere — or the server only asked
 * for a fresh connection. Socket.io does not reconnect after a server
 * disconnect, so without this the screen stayed on «Нет связи с сервером»
 * with voice controls that could do nothing. A gone session goes to sign-in;
 * a live one reconnects, with growing delays if the server keeps closing it.
 *
 * A disconnect that follows `auth:temporarily-unavailable` is the platform
 * being down: lib/socket.ts already retries it with backoff, so it is left alone.
 * Binding twice to the same socket is a no-op.
 */
export function bindServerKickRecheck(
  socket: KickedSocket,
  recheck: () => Promise<SessionVerdict>,
  onGone: () => void,
  timers: { now: () => number; later: (run: () => void, ms: number) => void } = {
    now: () => Date.now(),
    later: (run, ms) => { window.setTimeout(run, ms); },
  },
): void {
  if (bound.has(socket)) return;
  bound.add(socket);
  let platformDown = false;
  let lastReconnect = Number.NEGATIVE_INFINITY;
  let delay = 0;
  socket.on('auth:temporarily-unavailable', () => { platformDown = true; });
  socket.on('connect', () => { platformDown = false; });
  socket.on('disconnect', (reason: string) => {
    if (reason !== 'io server disconnect') return;
    if (platformDown) return;
    void recheck().then((verdict) => {
      if (verdict === 'gone') return onGone();
      if (verdict !== 'live' || socket.connected) return;
      delay = timers.now() - lastReconnect < KICK_QUIET_MS ? Math.min(MAX_DELAY_MS, Math.max(FIRST_DELAY_MS, delay * 2)) : 0;
      timers.later(() => {
        if (socket.connected) return;
        lastReconnect = timers.now();
        socket.connect();
      }, delay);
    });
  });
}
